import React from 'react';
import { SectionHeading, DetailRow } from './primitives';
import type { ProfileData } from './types';

interface AccountDetailsCardProps {
  profile: ProfileData;
  roleColor: string;
  lastLogin: string;
  workspaceCount: number;
  /** Players don't manage workspaces, so their workspace count is hidden. */
  showWorkspaces?: boolean;
  formatDate: (iso: string) => string;
  formatDateTime: (iso: string) => string;
}

const AccountDetailsCard: React.FC<AccountDetailsCardProps> = ({
  profile,
  roleColor,
  lastLogin,
  workspaceCount,
  showWorkspaces = true,
  formatDate,
  formatDateTime,
}) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8 dark:border-gray-700 dark:bg-gray-800">
    <div className="grid grid-cols-1 gap-x-12 gap-y-8 lg:grid-cols-2 lg:divide-x lg:divide-gray-100 dark:lg:divide-gray-700/60">
      <section>
        <SectionHeading
          icon={
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.5 19.5a7.5 7.5 0 0115 0v.75H4.5v-.75z" />
            </svg>
          }
        >
          Personal Details
        </SectionHeading>
        <dl>
          <DetailRow label="Full Name">{profile.name}</DetailRow>
          <DetailRow label="Email">{profile.email}</DetailRow>
          <DetailRow label="Role">
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ring-1 ring-inset ${roleColor}`}>
              {profile.role}
            </span>
          </DetailRow>
        </dl>
      </section>

      <section className="lg:pl-12">
        <SectionHeading
          icon={
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        >
          Account Details
        </SectionHeading>
        <dl>
          <DetailRow label="Member Since">{formatDate(profile.createdAt)}</DetailRow>
          <DetailRow label="Last Login">{lastLogin ? formatDateTime(lastLogin) : '—'}</DetailRow>
          {showWorkspaces && (
            <DetailRow label="Workspaces">
              {workspaceCount} {workspaceCount === 1 ? 'workspace' : 'workspaces'}
            </DetailRow>
          )}
          <DetailRow label="Account Status">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20 dark:bg-emerald-900/40 dark:text-emerald-300 dark:ring-emerald-400/20">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Active
            </span>
          </DetailRow>
        </dl>
      </section>
    </div>
  </div>
);

export default AccountDetailsCard;
